/**
 * useAgentInquiry
 *
 * Agent Progress 页面核心数据 hook：
 *  - 封装 inquiry 详情、Request Details、Pause/Resume、Replace、Rename 全部行为
 *  - 与原 contact-progress 实现 1:1 对齐：错误处理、toast、mixpanel 埋点、乐观更新策略
 *
 * 不订阅 WebSocket（由 useAgentProgressRefresh 统一管理；外部调用 refresh）。
 */

import { useCallback, useState } from 'react';
import { toast } from '@leadsnavi/ui-kit';
import mixpanel from 'mixpanel-browser';
import { inquiryDetailsGet, inquiryRequestGet, operateInquiryPost } from '@/services/swagger/easourcing/InquiryService';
import { agentReplacePost } from '@/services/swagger/easourcing/AgentInquiryService';
import type {
  AgentInquiryDetail,
  AgentInquiryStats,
  IApiInquiryDto,
  IApiInquiryRequestResp,
  UseAgentInquiryResult,
} from '../types';
import { useMemoizedFn } from 'ahooks';

export interface UseAgentInquiryOptions {
  inquiryId: string;
  sessionId?: string;
  onNameChange?: (name: string) => void;
  onStateChange?: () => void;
}

export function useAgentInquiry(options: UseAgentInquiryOptions): UseAgentInquiryResult {
  const { inquiryId, sessionId, onNameChange, onStateChange } = options;

  const [detail, setDetail] = useState<AgentInquiryDetail | null>(null);
  const [requestDetail, setRequestDetail] = useState<IApiInquiryRequestResp | null>(null);
  const [requestDetailLoading, setRequestDetailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [operating, setOperating] = useState(false);

  const stats = (detail?.stats ?? null) as AgentInquiryStats | null;

  /** 拉取整体详情 */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inquiryDetailsGet({ inquiryId });
      if (res?.data) {
        setDetail(res.data as AgentInquiryDetail);
      }
    } catch (error) {
      console.error('Failed to fetch inquiry detail:', error);
    } finally {
      setLoading(false);
    }
  }, [inquiryId]);

  /** 拉取 Request Detail（用于 Request Details 弹窗） */
  const fetchRequestDetail = useCallback(async () => {
    setRequestDetailLoading(true);
    try {
      const res = await inquiryRequestGet({ inquiryId });
      setRequestDetail(res?.data ?? null);
    } catch (error) {
      console.error('Failed to fetch inquiry request detail:', error);
      toast.error('Failed to load request details');
    } finally {
      setRequestDetailLoading(false);
    }
  }, [inquiryId]);

  /**
   * Pause / Resume 切换：
   *  - 当前 status === 1（暂停）→ Resume（operate=2）
   *  - 否则 → Pause（operate=1，含 mixpanel 埋点）
   *
   * Resume 时若返回 runningInquiries.length >= 3，**不**自动切状态，
   * 把 needConfirm + runningInquiries 抛给调用方，让其弹 ActiveTaskLimitModal。
   */
  const toggleRunning = useCallback(async (): Promise<{
    needConfirm: boolean;
    runningInquiries: IApiInquiryDto[];
  }> => {
    if (!stats) return { needConfirm: false, runningInquiries: [] };
    const isPaused = stats.status === 1;
    if (!isPaused) {
      mixpanel.track('stop_all_tasks_click');
    }
    const operate = isPaused ? 2 : 1;
    setOperating(true);
    try {
      const res = await operateInquiryPost({ inquiryId, operate, sessionId });

      if (isPaused) {
        const runningList = (res?.data?.runningInquiries || []) as IApiInquiryDto[];
        if (runningList.length >= 3) {
          // 待用户决策；状态保持 paused
          return { needConfirm: true, runningInquiries: runningList };
        }
      }

      // 乐观更新 status
      setDetail(prev =>
        prev?.stats ? { ...prev, stats: { ...prev.stats, status: isPaused ? 0 : 1 } as AgentInquiryStats } : prev
      );
      // 异步刷新真实状态
      void refresh();
      onStateChange?.();
      toast.success(isPaused ? 'Inquiry resumed' : 'Inquiry paused');
      return { needConfirm: false, runningInquiries: [] };
    } catch (error) {
      console.error('Failed to operate all tasks:', error);
      toast.error('Operation failed');
      return { needConfirm: false, runningInquiries: [] };
    } finally {
      setOperating(false);
    }
  }, [stats, inquiryId, sessionId, refresh, onStateChange]);

  /** 替换其中一个 running inquiry，恢复当前 inquiry */
  const replaceRunningInquiry = useMemoizedFn(async (runningInquiryId: string) => {
    setOperating(true);
    try {
      await agentReplacePost({ stopInquiryId: runningInquiryId, startInquiryId: inquiryId });
      toast.success('Task resumed successfully');
      await refresh();
      onStateChange?.();
    } catch (error) {
      console.error('Failed to replace task:', error);
      toast.error('Operation failed');
    } finally {
      setOperating(false);
    }
  });

  /** 重命名（operate=3）：乐观更新 + 失败抛错让弹窗保留 */
  const renameInquiry = useCallback(
    async (newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed) {
        throw new Error('Name cannot be empty');
      }
      if (stats?.inquiryName === trimmed) {
        // 名称未变，直接成功（不发请求）
        return;
      }
      setOperating(true);
      try {
        await operateInquiryPost({
          inquiryId,
          operate: 3,
          taskName: trimmed,
          sessionId,
        });
        // 乐观更新 stats.inquiryName
        setDetail(prev =>
          prev?.stats ? { ...prev, stats: { ...prev.stats, inquiryName: trimmed } as AgentInquiryStats } : prev
        );
        onNameChange?.(trimmed);
        onStateChange?.();
        toast.success('Name updated successfully');
      } catch (error) {
        console.error('Failed to update inquiry name:', error);
        toast.error('Failed to update name');
        throw error;
      } finally {
        setOperating(false);
      }
    },
    [stats, inquiryId, sessionId, onNameChange, onStateChange]
  );

  return {
    detail,
    stats,
    requestDetail,
    requestDetailLoading,
    loading,
    operating,
    refresh,
    fetchRequestDetail,
    toggleRunning,
    replaceRunningInquiry,
    renameInquiry,
  };
}

export default useAgentInquiry;
